import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext.jsx";
import { useNotifications } from "../../context/NotificationContext.jsx";

/**
 * Topbar — barre de titre affichée en haut de chaque page.
 *
 * Props :
 *  - title    : première partie du titre (ex: "Mon")
 *  - subtitle : deuxième partie, mise en valeur (ex: "profil")
 *  - admin    : true pour le portail admin — n'affiche pas de comportement
 *               différent visuellement pour l'instant, gardé pour compat API
 *  - actions  : contenu optionnel affiché à droite (boutons spécifiques à la page)
 *
 * Utilise NotificationContext (unreadCount) et AuthContext (user) partagés
 * avec Sidebar via AppLayout → NotificationProvider.
 */
export default function Topbar({ title, subtitle, admin, actions }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { unreadCount } = useNotifications();

  const initials = `${user?.firstName?.[0] ?? ""}${user?.lastName?.[0] ?? ""}`;

  return (
    <div className="itm-topbar">
      <div className="itm-topbar-title">
        {title} {subtitle && <span>{subtitle}</span>}
      </div>

      <div className="itm-topbar-search">
        <i className="bi bi-search" />
        <input type="text" placeholder="Rechercher…" />
      </div>

      <div className="itm-topbar-actions">
        {actions}

        <button
          className="itm-topbar-btn"
          onClick={() => navigate("/notifications")}
          title="Notifications"
        >
          <i className="bi bi-bell" />
          {unreadCount > 0 && <span className="itm-topbar-dot" />}
        </button>

        <div
          className="itm-topbar-avatar"
          onClick={() => navigate("/profile")}
          title={user ? `${user.firstName} ${user.lastName}` : "Profil"}
        >
          {initials || <i className="bi bi-person-fill" />}
        </div>
      </div>

      <style>{CSS}</style>
    </div>
  );
}

const CSS = `
.itm-topbar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 24px;
  background: #fff;
  border-bottom: 1px solid rgba(0,0,0,.08);
  position: sticky;
  top: 0;
  z-index: 50;
}
.itm-topbar-title {
  font-family: "Syne", sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #1A1A1A;
  white-space: nowrap;
}
.itm-topbar-title span { color: #E63329; }

.itm-topbar-search {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #F4F5F7;
  border: 1px solid rgba(0,0,0,.07);
  border-radius: 10px;
  padding: 7px 12px;
  max-width: 320px;
  margin-left: auto;
}
.itm-topbar-search i { font-size: 13px; color: #9CA3AF; flex-shrink: 0; }
.itm-topbar-search input {
  border: none; background: none; outline: none;
  font-size: 13px; color: #1A1A1A; width: 100%;
}
.itm-topbar-search input::placeholder { color: #9CA3AF; }

.itm-topbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.itm-topbar-btn {
  position: relative;
  width: 36px; height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(0,0,0,.08);
  background: #fff;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  color: #4B5563;
  font-size: 15px;
  transition: all .15s;
}
.itm-topbar-btn:hover { background: #F4F5F7; color: #1A1A1A; }

.itm-topbar-dot {
  position: absolute;
  top: 7px; right: 7px;
  width: 8px; height: 8px;
  border-radius: 50%;
  background: #E63329;
  border: 1.5px solid #fff;
}

.itm-topbar-avatar {
  width: 36px; height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg,#E63329,#c92b22);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; text-transform: uppercase;
  cursor: pointer;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .itm-topbar-search { display: none; }
}
`;